/**
 * 屋台の夜をイメージしたプロシージャル BGM。
 *
 * 音源ファイルを使わず、WebAudio で「琴風の分散和音＋やわらかい低音＋
 * シェイカー＋風鈴」を組み立ててループさせる。
 *
 * ・先読みスケジューラ（lookahead）方式なので setInterval のゆらぎに強い
 * ・intensity を上げると層が増え、夜が深まるほど賑やかになる
 * ・停止時はスケジュールを止めるだけなので CPU を食わない
 */

import { envelope, type AudioBus } from './AudioEngine';
import { scaleSemitone, semitoneToHz, YO_SCALE_SEMITONES } from './scale';

/** 1拍あたりの秒数を決めるテンポ。 */
const BPM = 76;
/** 1小節あたりのステップ数（8分音符）。 */
const STEPS_PER_BAR = 8;
/** ループの長さ（小節）。 */
const BARS = 4;
const TOTAL_STEPS = STEPS_PER_BAR * BARS;

/** 小節ごとのコード基音（A を 0 とした半音）。 */
const BAR_ROOTS = [0, -3, 5, 7];

/** 琴の分散和音を鳴らすステップと、音階上の位置。 */
const ARPEGGIO: Array<{ step: number; degree: number; gain: number }> = [
  { step: 0, degree: 7, gain: 0.16 },
  { step: 2, degree: 9, gain: 0.11 },
  { step: 3, degree: 8, gain: 0.08 },
  { step: 5, degree: 10, gain: 0.13 },
  { step: 6, degree: 7, gain: 0.09 },
];

/** 基準音（A3）。 */
const KEY_HZ = 220;

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_SEC = 0.12;

export class ProceduralBgm {
  private readonly bus: AudioBus;
  private readonly out: GainNode;
  private timer: ReturnType<typeof setInterval> | null = null;
  private step = 0;
  private nextStepTime = 0;
  private intensity = 0;

  constructor(bus: AudioBus) {
    this.bus = bus;
    this.out = bus.ctx.createGain();
    this.out.gain.value = 0.55;
    this.out.connect(bus.out);
  }

  get playing(): boolean {
    return this.timer !== null;
  }

  start(): void {
    if (this.timer) return;
    this.nextStepTime = this.bus.ctx.currentTime + 0.06;
    this.timer = setInterval(() => this.tick(), LOOKAHEAD_MS);
  }

  stop(): void {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  /** 0（静か）〜1（賑やか）。到達レベルに応じて盛り上げる。 */
  setIntensity(value: number): void {
    this.intensity = Math.min(1, Math.max(0, value));
  }

  dispose(): void {
    this.stop();
    try {
      this.out.disconnect();
    } catch {
      /* noop */
    }
  }

  private get stepSeconds(): number {
    return 60 / BPM / (STEPS_PER_BAR / 4);
  }

  private tick(): void {
    const ctx = this.bus.ctx;
    if (ctx.state === 'suspended') return;
    const horizon = ctx.currentTime + SCHEDULE_AHEAD_SEC;
    let guard = 0;
    while (this.nextStepTime < horizon && guard < 32) {
      this.scheduleStep(this.step, this.nextStepTime);
      this.nextStepTime += this.stepSeconds;
      this.step = (this.step + 1) % TOTAL_STEPS;
      guard += 1;
    }
  }

  private scheduleStep(step: number, at: number): void {
    const bar = Math.floor(step / STEPS_PER_BAR);
    const inBar = step % STEPS_PER_BAR;
    const root = BAR_ROOTS[bar % BAR_ROOTS.length];
    const intensity = this.intensity;

    // 低音（小節あたま と 3拍目）
    if (inBar === 0 || inBar === 4) {
      this.bass(semitoneToHz(root - 12, KEY_HZ), at, inBar === 0 ? 0.2 : 0.13);
    }

    // 太鼓（小節あたま、後半の小節ほど強く）
    if (inBar === 0) {
      this.taiko(at, 0.16 + intensity * 0.08);
    } else if (inBar === 6 && bar % 2 === 1) {
      this.taiko(at, 0.09 + intensity * 0.06);
    }

    // 琴の分散和音
    const note = ARPEGGIO.find((entry) => entry.step === inBar);
    if (note) {
      const hz = semitoneToHz(root + scaleSemitone(YO_SCALE_SEMITONES, note.degree), KEY_HZ);
      this.koto(hz, at, note.gain * (0.75 + intensity * 0.45));
    }

    // シェイカー（裏拍。賑やかさが上がるほどはっきり）
    if (inBar % 2 === 1 && intensity > 0.05) {
      this.shaker(at, 0.012 + intensity * 0.026);
    }

    // 風鈴（ループの節目に一度だけ）
    if (step === 0 && intensity > 0.45) {
      const hz = semitoneToHz(root + scaleSemitone(YO_SCALE_SEMITONES, 14), KEY_HZ);
      this.bell(hz, at + this.stepSeconds * 0.5, 0.07 * intensity);
    }
  }

  private koto(hz: number, at: number, gain: number): void {
    const ctx = this.bus.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(hz, at);

    const partial = ctx.createOscillator();
    partial.type = 'sine';
    partial.frequency.setValueAtTime(hz * 3.01, at);
    const partialGain = ctx.createGain();
    partialGain.gain.value = 0.14;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(hz * 7 + 700, at);
    filter.frequency.exponentialRampToValueAtTime(Math.max(200, hz * 1.4), at + 0.9);

    const amp = ctx.createGain();
    envelope(amp, at, gain, 0.006, 0.9);

    osc.connect(amp);
    partial.connect(partialGain).connect(amp);
    amp.connect(filter).connect(this.out);

    osc.start(at);
    partial.start(at);
    osc.stop(at + 1.05);
    partial.stop(at + 1.05);
  }

  private bass(hz: number, at: number, gain: number): void {
    const ctx = this.bus.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(hz, at);
    const amp = ctx.createGain();
    envelope(amp, at, gain, 0.02, 0.75);
    osc.connect(amp).connect(this.out);
    osc.start(at);
    osc.stop(at + 0.85);
  }

  private taiko(at: number, gain: number): void {
    const ctx = this.bus.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(126, at);
    osc.frequency.exponentialRampToValueAtTime(52, at + 0.18);
    const amp = ctx.createGain();
    envelope(amp, at, gain, 0.004, 0.24);
    osc.connect(amp).connect(this.out);
    osc.start(at);
    osc.stop(at + 0.3);
  }

  private shaker(at: number, gain: number): void {
    const ctx = this.bus.ctx;
    const source = ctx.createBufferSource();
    source.buffer = this.bus.noise;
    source.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 5200;
    const amp = ctx.createGain();
    envelope(amp, at, gain, 0.003, 0.07);
    source.connect(filter).connect(amp).connect(this.out);
    source.start(at);
    source.stop(at + 0.12);
  }

  private bell(hz: number, at: number, gain: number): void {
    const ctx = this.bus.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(hz, at);
    const amp = ctx.createGain();
    envelope(amp, at, gain, 0.004, 1.6);
    osc.connect(amp).connect(this.out);
    osc.start(at);
    osc.stop(at + 1.7);
  }
}
